import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { sendOrderConfirmation, sendAdminNotification } from '../services/emailService';
import { loadRazorpayScript, createRazorpayOrder } from '../services/razorpayService';
import { 
  FiMapPin, FiPlus, FiCheck, FiArrowLeft, FiPackage,
  FiCreditCard, FiTrash2, FiEdit2, FiSave, FiGift, FiTruck,
  FiTag, FiX
} from 'react-icons/fi';
import styles from './Checkout.module.css';

const GIFT_PACKING_CHARGE = 50;
const DELIVERY_CHARGE = 70;
const FREE_DELIVERY_THRESHOLD = 499;

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];

const CouponBlock = ({
  mobile,
  appliedCoupon,
  discount,
  couponCode,
  setCouponCode,
  couponError,
  couponSuccess,
  applyingCoupon,
  applyCoupon,
  removeCoupon,
}) => (
  <div className={mobile ? styles.mobileCouponSection : styles.couponSection}>
    {appliedCoupon ? (
      <div className={mobile ? styles.mobileAppliedCoupon : styles.appliedCoupon}>
        <span className={styles.couponInfo}>
          <FiTag /> {appliedCoupon.code}
        </span>
        <span className={styles.discount}>-₹{discount}</span>
        <button
          onClick={removeCoupon}
          className={mobile ? styles.mobileRemoveCoupon : styles.removeCouponBtn}
        >
          <FiX />
        </button>
      </div>
    ) : (
      <div className={mobile ? styles.mobileCouponInput : styles.couponInput}>
        <input
          type="text"
          placeholder="Enter coupon code"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          className={mobile ? styles.mobileCouponField : ''}
        />
        <button
          onClick={applyCoupon}
          disabled={applyingCoupon}
          className={mobile ? styles.mobileApplyCouponBtn : styles.applyCouponBtn}
        >
          Apply
        </button>
      </div>
    )}
    {couponError && (
      <div className={mobile ? styles.mobileCouponError : styles.couponError}>{couponError}</div>
    )}
    {couponSuccess && (
      <div className={mobile ? styles.mobileCouponSuccess : styles.couponSuccess}>{couponSuccess}</div>
    )}
  </div>
);

const AddressForm = ({
  editingAddress,
  newAddressForm,
  setNewAddressForm,
  handleSaveAddress,
  setShowNewAddress,
  setEditingAddress,
}) => (
  <div className={styles.newAddressForm}>
    <h3>{editingAddress !== null ? 'Edit Address' : 'Add New Address'}</h3>
    <div className={styles.formRow}>
      <div className={styles.formGroup}>
        <label>Full Name <span className={styles.required}>*</span></label>
        <input type="text" placeholder="Enter full name" value={newAddressForm.name} onChange={(e) => setNewAddressForm(prev => ({...prev, name: e.target.value}))} />
      </div>
      <div className={styles.formGroup}>
        <label>Mobile Number <span className={styles.required}>*</span></label>
        <input type="tel" placeholder="10 digit mobile number" value={newAddressForm.mobile} onChange={(e) => setNewAddressForm(prev => ({...prev, mobile: e.target.value}))} />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formGroup}>
        <label>Door/House No <span className={styles.required}>*</span></label>
        <input type="text" placeholder="House/Building No." value={newAddressForm.doorNo} onChange={(e) => setNewAddressForm(prev => ({...prev, doorNo: e.target.value}))} />
      </div>
      <div className={styles.formGroup}>
        <label>Street Address <span className={styles.required}>*</span></label>
        <input type="text" placeholder="Street name" value={newAddressForm.street} onChange={(e) => setNewAddressForm(prev => ({...prev, street: e.target.value}))} />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formGroup}>
        <label>Landmark</label>
        <input type="text" placeholder="Nearby landmark (optional)" value={newAddressForm.landmark} onChange={(e) => setNewAddressForm(prev => ({...prev, landmark: e.target.value}))} />
      </div>
      <div className={styles.formGroup}>
        <label>City <span className={styles.required}>*</span></label>
        <input type="text" placeholder="City name" value={newAddressForm.city} onChange={(e) => setNewAddressForm(prev => ({...prev, city: e.target.value}))} />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formGroup}>
        <label>State</label>
        <select value={newAddressForm.state} onChange={(e) => setNewAddressForm(prev => ({...prev, state: e.target.value}))}>
          {STATES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div className={styles.formGroup}>
        <label>PIN Code <span className={styles.required}>*</span></label>
        <input type="text" placeholder="6 digit PIN code" value={newAddressForm.pincode} onChange={(e) => setNewAddressForm(prev => ({...prev, pincode: e.target.value}))} maxLength="6" />
      </div>
    </div>
    <div className={styles.checkboxGroup}>
      <label>
        <input type="checkbox" checked={newAddressForm.isDefault} onChange={(e) => setNewAddressForm(prev => ({...prev, isDefault: e.target.checked}))} />
        Set as default address
      </label>
    </div>
    <div className={styles.formActions}>
      <button type="button" onClick={() => { setShowNewAddress(false); setEditingAddress(null); }} className={styles.cancelBtn}>Cancel</button>
      <button type="button" onClick={handleSaveAddress} className={styles.saveAddressBtn}>
        <FiSave /> {editingAddress !== null ? 'Update Address' : 'Save Address'}
      </button>
    </div>
  </div>
);

const Checkout = () => {
  const [cartItems, setCartItems] = useState([]);
  const [cartHampers, setCartHampers] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [message, setMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  // COD removed — Razorpay is the only payment method now
  const paymentMethod = 'RAZORPAY';
  const [saleMap, setSaleMap] = useState({});
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [newAddressForm, setNewAddressForm] = useState({
    name: '', mobile: '', doorNo: '', street: '', city: '',
    state: 'Andhra Pradesh', pincode: '', landmark: '', isDefault: false
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadRazorpayScript();
    fetchData();
  }, []);

  const fetchActiveSales = async () => {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        discount_type,
        discount_value,
        sale_products ( product_id )
      `)
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now);

    if (error || !data) return {};

    const map = {};
    data.forEach(sale => {
      sale.sale_products?.forEach(sp => {
        if (!map[sp.product_id]) {
          map[sp.product_id] = {
            discount_type: sale.discount_type,
            discount_value: sale.discount_value
          };
        }
      });
    });
    return map;
  };

  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }

      const { data: profile } = await supabase
        .from('users').select('*').eq('id', user.id).single();

      if (profile) {
        setUserProfile(profile);
        let addresses = [];
        if (profile.saved_addresses) {
          addresses = typeof profile.saved_addresses === 'string'
            ? JSON.parse(profile.saved_addresses)
            : profile.saved_addresses;
        }
        setSavedAddresses(addresses);
        const defaultAddr = addresses.find(addr => addr.isDefault);
        if (defaultAddr) setSelectedAddress(defaultAddr);
      }

      const sales = await fetchActiveSales();
      setSaleMap(sales);

      const { data: cart } = await supabase
        .from('cart_items')
        .select('*, products(*)')
        .eq('user_id', user.id);

      const { data: hampers } = await supabase
        .from('cart_hampers')
        .select('*, hampers!cart_hampers_hamper_id_fkey (*)')
        .eq('user_id', user.id);

      const hasItems = (cart && cart.length > 0) || (hampers && hampers.length > 0);
      if (!hasItems) {
        navigate('/cart');
        return;
      }

      setCartItems(cart || []);
      setCartHampers(hampers || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDiscountedPrice = (product) => {
  if (!product) return 0;
  const sale = saleMap[product.id];
  if (!sale) return product.price;
  let price;
  if (sale.discount_type === 'percentage') {
    price = product.price * (1 - sale.discount_value / 100);
  } else {
    price = Math.max(0, product.price - sale.discount_value);
  }
  return Math.round(price * 100) / 100; // round to 2 decimals here
};
  // ── Totals ───────────────────────────────────────────────────────────────
  const calculateSubtotal = () => {
  const productsTotal = cartItems.reduce(
    (total, item) => total + getDiscountedPrice(item.products) * item.quantity, 0
  );
  const hampersTotal = cartHampers.reduce(
    (total, h) => total + (h.hampers?.price || 0) * h.quantity, 0
  );
  return Math.round((productsTotal + hampersTotal) * 100) / 100;
};

  const calculateGiftCharges = () =>
    cartItems.filter(i => i.gift_packing).length * GIFT_PACKING_CHARGE;

  const calculateDeliveryCharge = () => {
    const subtotal = calculateSubtotal();
    return subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_CHARGE;
  };

  const calculateCGSTAndSGST = () => {
    const base = calculateSubtotal() + calculateGiftCharges();
    const totalGST = Math.round(base * 0.05);
    let cgst = Math.round(totalGST / 2);
    let sgst = totalGST - cgst;
    if (cgst + sgst !== totalGST) {
      cgst += totalGST - (cgst + sgst);
    }
    return { cgst, sgst };
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const giftCharges = calculateGiftCharges();
    const deliveryCharge = calculateDeliveryCharge();
    const { cgst, sgst } = calculateCGSTAndSGST();
    const base = subtotal + giftCharges + deliveryCharge + cgst + sgst;
    return Math.max(0, base - discount);
  };

  const totalItemsCount = cartItems.length + cartHampers.length;
  // ────────────────────────────────────────────────────────────────────────

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      setCouponSuccess('');
      return;
    }

    setApplyingCoupon(true);
    setCouponError('');
    setCouponSuccess('');

    // NEW: Coupons require an authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setCouponError('Please login to use a coupon code');
      setApplyingCoupon(false);
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('code', couponCode.toUpperCase().trim())
      .eq('is_active', true)
      .lte('valid_from', today)
      .or(`valid_to.is.null,valid_to.gte.${today}`)
      .maybeSingle();

    if (error || !data) {
      setCouponError('Invalid or expired coupon');
      setApplyingCoupon(false);
      return;
    }

    if (data.usage_limit && data.used_count >= data.usage_limit) {
      setCouponError('Coupon usage limit exceeded');
      setApplyingCoupon(false);
      return;
    }

    // NEW: Check how many times THIS user has already used this coupon
    const perCustomerLimit = data.per_customer_limit ?? 1;
    const { count: userUsageCount, error: usageCheckError } = await supabase
      .from('coupon_usages')
      .select('id', { count: 'exact', head: true })
      .eq('coupon_id', data.id)
      .eq('user_id', user.id);

    if (usageCheckError) {
      setCouponError('Unable to verify coupon eligibility. Please try again.');
      setApplyingCoupon(false);
      return;
    }

    if ((userUsageCount || 0) >= perCustomerLimit) {
      setCouponError('You have already used this coupon the maximum allowed times.');
      setApplyingCoupon(false);
      return;
    }

    const eligibleSubtotal = calculateSubtotal() + calculateGiftCharges();
    if (eligibleSubtotal < data.min_order_value) {
      setCouponError(`Min order ₹${data.min_order_value} required`);
      setApplyingCoupon(false);
      return;
    }

    let discountAmount = data.discount_type === 'percentage'
      ? eligibleSubtotal * (data.discount_value / 100)
      : data.discount_value;

    if (data.max_discount && discountAmount > data.max_discount) {
      discountAmount = data.max_discount;
    }

    discountAmount = Math.round(discountAmount);

    setAppliedCoupon(data);
    setDiscount(discountAmount);
    setCouponSuccess(`Coupon applied! You saved ₹${discountAmount}`);
    setCouponCode('');
    setApplyingCoupon(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponSuccess('');
    setCouponError('');
    setCouponCode('');
  };

  const handleSaveAddress = async () => {
    if (!newAddressForm.name || !newAddressForm.mobile || !newAddressForm.doorNo ||
        !newAddressForm.street || !newAddressForm.city || !newAddressForm.pincode) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const formattedAddress = {
      id: editingAddress !== null ? savedAddresses[editingAddress]?.id : Date.now(),
      ...newAddressForm,
      fullAddress: `${newAddressForm.doorNo}, ${newAddressForm.street}${newAddressForm.landmark ? ', ' + newAddressForm.landmark : ''}, ${newAddressForm.city}, ${newAddressForm.state}, ${newAddressForm.pincode}`
    };

    let updatedAddresses = [...savedAddresses];
    if (newAddressForm.isDefault) {
      updatedAddresses = updatedAddresses.map(addr => ({ ...addr, isDefault: false }));
    }
    if (editingAddress !== null) {
      updatedAddresses[editingAddress] = formattedAddress;
    } else {
      updatedAddresses.push(formattedAddress);
    }
    setSavedAddresses(updatedAddresses);

    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('users').update({ saved_addresses: JSON.stringify(updatedAddresses) }).eq('id', user.id);
    if (formattedAddress.isDefault) setSelectedAddress(formattedAddress);
    setShowNewAddress(false);
    setEditingAddress(null);
    setNewAddressForm({ name: '', mobile: '', doorNo: '', street: '', city: '', state: 'Andhra Pradesh', pincode: '', landmark: '', isDefault: false });
    setMessage({ type: 'success', text: editingAddress !== null ? 'Address updated!' : 'Address added!' });
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteAddress = async (index) => {
    if (!window.confirm('Delete this address?')) return;
    const updatedAddresses = savedAddresses.filter((_, i) => i !== index);
    setSavedAddresses(updatedAddresses);
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('users').update({ saved_addresses: JSON.stringify(updatedAddresses) }).eq('id', user.id);
    if (selectedAddress?.id === savedAddresses[index]?.id) setSelectedAddress(null);
    setMessage({ type: 'success', text: 'Address deleted!' });
    setTimeout(() => setMessage(''), 3000);
  };

  const handleEditAddress = (address, index) => {
    setNewAddressForm({
      name: address.name || '', mobile: address.mobile || '', doorNo: address.doorNo || '',
      street: address.street || '', city: address.city || '', state: address.state || 'Andhra Pradesh',
      pincode: address.pincode || '', landmark: address.landmark || '', isDefault: address.isDefault || false
    });
    setEditingAddress(index);
    setShowNewAddress(true);
  };

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    setShowNewAddress(false);
    setEditingAddress(null);
  };

  // NEW: accepts couponInfo ({ id, code, discountAmount }) so we can log usage
  // only once the payment has actually succeeded — not just on order creation.
  const processRazorpayPayment = async (order, totalAmount, couponInfo) => {
    try {
      const razorpayOrder = await createRazorpayOrder(totalAmount, order.order_number);
      const options = {
        key: 'rzp_live_SnFBYmQC6vompt',
        amount: razorpayOrder.amount,
        currency: 'INR',
        name: 'Etikoppaka Toys',
        description: `Order #${order.order_number}`,
        image: '/logo1.png',
        order_id: razorpayOrder.id,
        prefill: { name: userProfile?.name || '', email: userProfile?.email || '', contact: userProfile?.mobile || '' },
        theme: { color: '#1F5B3A' },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            setMessage({ type: 'error', text: 'Payment cancelled' });
            setTimeout(() => setMessage(''), 3000);
          }
        },
        handler: async (response) => {
          const { data: { user } } = await supabase.auth.getUser();
          await supabase.from('orders').update({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            payment_status: 'paid',
          }).eq('id', order.id);
          await supabase.from('cart_items').delete().eq('user_id', user.id);
          await supabase.from('cart_hampers').delete().eq('user_id', user.id);

          // NEW: log coupon usage only after payment is confirmed successful.
          // This keeps per-customer-limit checks accurate — an abandoned
          // Razorpay modal should not consume the customer's coupon use.
          if (couponInfo) {
            const { error: usageLogError } = await supabase
              .from('coupon_usages')
              .insert({
                coupon_id: couponInfo.id,
                user_id: user.id,
                order_id: order.id,
                amount_discounted: couponInfo.discountAmount,
              });

            if (usageLogError) {
              // Don't block the success flow over a logging failure —
              // the payment has already gone through. Just log it for review.
              console.error('Failed to log coupon usage:', usageLogError);
            }

            await supabase
              .from('coupons')
              .update({ used_count: (couponInfo.used_count || 0) + 1 })
              .eq('id', couponInfo.id);
          }

          // send confirmation emails after successful online payment
          const orderDetails = {
            order_number: order.order_number,
            total_amount: totalAmount,
            created_at: order.created_at,
            shipping_address: order.shipping_address,
            items: order.order_items,
            payment_method: 'RAZORPAY',
            gift_charges: calculateGiftCharges(),
            delivery_charge: calculateDeliveryCharge(),
            cgst: order.cgst,
            sgst: order.sgst,
            discount_amount: order.discount_amount,
            coupon_code: order.coupon_code,
          };
          await sendOrderConfirmation(orderDetails, user.email, userProfile?.name || 'Customer', {
            paymentMethod: 'RAZORPAY',
            deliveryCharge: calculateDeliveryCharge()
          });
          await sendAdminNotification(orderDetails, {
            name: userProfile?.name || 'Customer',
            email: user.email,
            mobile: userProfile?.mobile || 'Not provided',
            paymentMethod: 'RAZORPAY'
          });

          navigate('/orders', { state: { orderSuccess: true, orderNumber: order.order_number, paymentSuccess: true } });
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Razorpay error:', error);
      setMessage({ type: 'error', text: 'Failed to initialize payment. Please try again.' });
      setTimeout(() => setMessage(''), 3000);
      setSubmitting(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      setMessage({ type: 'error', text: 'Please select a shipping address' });
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // NEW: coupons (and orders in general) require an authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setMessage({ type: 'error', text: 'Please login to place an order' });
      setTimeout(() => setMessage(''), 3000);
      navigate('/login');
      return;
    }

    setSubmitting(true);

    try {
      const totalAmount = calculateTotal();
      const deliveryCharge = calculateDeliveryCharge();
      const { cgst, sgst } = calculateCGSTAndSGST();
      const fullAddress = selectedAddress.fullAddress ||
        `${selectedAddress.doorNo}, ${selectedAddress.street}, ${selectedAddress.city} - ${selectedAddress.pincode}`;

      // NEW: re-verify the per-customer coupon limit right before placing
      // the order, in case the user applied the coupon a while ago and has
      // since placed another order with it in another tab/device.
      if (appliedCoupon) {
        const perCustomerLimit = appliedCoupon.per_customer_limit ?? 1;
        const { count: userUsageCount, error: usageCheckError } = await supabase
          .from('coupon_usages')
          .select('id', { count: 'exact', head: true })
          .eq('coupon_id', appliedCoupon.id)
          .eq('user_id', user.id);

        if (usageCheckError) {
          setMessage({ type: 'error', text: 'Unable to verify coupon eligibility. Please try again.' });
          setTimeout(() => setMessage(''), 3000);
          setSubmitting(false);
          return;
        }

        if ((userUsageCount || 0) >= perCustomerLimit) {
          setMessage({ type: 'error', text: 'You have already used this coupon the maximum allowed times.' });
          setTimeout(() => setMessage(''), 3000);
          removeCoupon();
          setSubmitting(false);
          return;
        }
      }

      // payment_method/payment_status are always Razorpay/initiated now
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          total_amount: totalAmount,
          shipping_address: fullAddress,
          payment_method: 'RAZORPAY',
          payment_status: 'initiated',
          status: 'pending',
          delivery_charge: deliveryCharge,
          cgst,
          sgst,
          discount_amount: discount,
          coupon_code: appliedCoupon?.code || null,
        })
        .select()
        .single();

      if (orderError) throw orderError;

      const productOrderItems = cartItems.map(item => ({
        order_id: order.id,
        product_id: item.product_id,
        product_name: item.products.name,
        quantity: item.quantity,
        price: Math.round(getDiscountedPrice(item.products)),
        gift_packing: !!item.gift_packing,
        gift_quote: item.gift_packing ? (item.gift_quote || '') : '',
        gift_charge: item.gift_packing ? GIFT_PACKING_CHARGE : 0,
        item_type: 'product',
      }));

      const hamperOrderItems = cartHampers.map(h => ({
        order_id: order.id,
        product_id: null,
        product_name: h.hampers?.name || 'Gift Hamper',
        quantity: h.quantity,
        price: h.hampers?.price || 0,
        gift_packing: false,
        gift_quote: '',
        gift_charge: 0,
        item_type: 'hamper',
        hamper_id: h.hamper_id,
      }));

      const orderItems = [...productOrderItems, ...hamperOrderItems];

      const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
      if (itemsError) throw itemsError;

      order.order_items = orderItems;

      // NOTE: coupon_usages logging + coupons.used_count increment now happen
      // inside processRazorpayPayment's success handler, AFTER payment is
      // confirmed — see couponInfo passed below. This avoids counting a
      // coupon as "used" if the customer abandons the Razorpay modal.
      const couponInfo = appliedCoupon
        ? { id: appliedCoupon.id, code: appliedCoupon.code, discountAmount: discount, used_count: appliedCoupon.used_count }
        : null;

      // always go through Razorpay — COD branch removed
      await processRazorpayPayment(order, totalAmount, couponInfo);
      setSubmitting(false);
    } catch (error) {
      console.error('Order error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to place order' });
      setTimeout(() => setMessage(''), 3000);
      setSubmitting(false);
    }
  };

  const giftCharges = calculateGiftCharges();
  const deliveryCharge = calculateDeliveryCharge();
  const subtotal = calculateSubtotal();
  const { cgst, sgst } = calculateCGSTAndSGST();
  const giftItems = cartItems.filter(i => i.gift_packing);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading checkout...</p>
      </div>
    );
  }

  const couponProps = {
    appliedCoupon, discount, couponCode, setCouponCode,
    couponError, couponSuccess, applyingCoupon, applyCoupon, removeCoupon,
  };

  const addressFormProps = {
    editingAddress, newAddressForm, setNewAddressForm,
    handleSaveAddress, setShowNewAddress, setEditingAddress,
  };

  // ============================================
  // MOBILE CHECKOUT UI
  // ============================================
  if (isMobile) {
    return (
      <div className={styles.mobileCheckout}>
        <div className={styles.mobileHeader}>
          <button onClick={() => navigate('/cart')} className={styles.mobileBackBtn}><FiArrowLeft /></button>
          <h1>Checkout</h1>
          <div className={styles.mobilePlaceholder}></div>
        </div>

        <div className={styles.mobileScrollContent}>
          {/* Address Section */}
          <div className={styles.mobileSection}>
            <div className={styles.mobileSectionHeader}><FiMapPin /><h2>Delivery Address</h2></div>
            {savedAddresses.map((address, index) => (
              <div key={address.id || index} className={`${styles.mobileAddressCard} ${selectedAddress?.id === address.id ? styles.selected : ''}`} onClick={() => handleSelectAddress(address)}>
                <div className={styles.mobileAddressRadio}>{selectedAddress?.id === address.id && <FiCheck />}</div>
                <div className={styles.mobileAddressDetails}>
                  <p className={styles.mobileAddressName}>{address.name}</p>
                  <p className={styles.mobileAddressPhone}>📞 {address.mobile}</p>
                  <p>{address.doorNo}, {address.street}</p>
                  <p>{address.city}, {address.state} - {address.pincode}</p>
                </div>
                <div className={styles.mobileAddressActions}>
                  <button onClick={(e) => { e.stopPropagation(); handleEditAddress(address, index); }}><FiEdit2 /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteAddress(index); }}><FiTrash2 /></button>
                </div>
              </div>
            ))}
            <button className={styles.mobileAddAddressBtn} onClick={() => setShowNewAddress(!showNewAddress)}>
              <FiPlus /> {showNewAddress ? 'Cancel' : 'Add New Address'}
            </button>
            {showNewAddress && <AddressForm {...addressFormProps} />}
          </div>

          {/* Payment Method — Razorpay only, no selection needed */}
          <div className={styles.mobileSection}>
            <div className={styles.mobileSectionHeader}><FiCreditCard /><h2>Payment Method</h2></div>
            <div className={styles.mobilePaymentOptions}>
              <div className={`${styles.mobilePaymentOption} ${styles.selected}`}>
                <span>💳</span>
                <div><strong>Card/UPI/NetBanking</strong><p>Pay securely via Razorpay</p></div>
                <FiCheck />
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className={styles.mobileOrderSummaryCard}>
            <h3>Order Summary</h3>

            {cartItems.map(item => {
              const salePrice = getDiscountedPrice(item.products);
              const isOnSale = salePrice < item.products.price;
              const lineTotal = salePrice * item.quantity + (item.gift_packing ? GIFT_PACKING_CHARGE : 0);
              return (
                <div key={`product-${item.id}`} className={styles.mobileSummaryRow}>
                  <span>
                    {item.products.name} x{item.quantity}
                    {item.gift_packing && <span className={styles.giftTag}> 🎁 Gift</span>}
                    {isOnSale && <span style={{ color: '#1F5B3A', fontSize: '0.75rem', marginLeft: 4 }}> (Sale)</span>}
                  </span>
                  <span>
                    {isOnSale && (
                      <span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 4, fontSize: '0.85em' }}>
                        ₹{item.products.price * item.quantity}
                      </span>
                    )}
                    ₹{Math.round(lineTotal)}
                  </span>
                </div>
              );
            })}

            {cartHampers.map(h => (
              <div key={`hamper-${h.id}`} className={styles.mobileSummaryRow}>
                <span>
                  🎁 {h.hampers?.name} x{h.quantity}
                  <span className={styles.giftTag}> Hamper</span>
                </span>
                <span>₹{(h.hampers?.price || 0) * h.quantity}</span>
              </div>
            ))}

            {giftCharges > 0 && (
              <div className={styles.mobileSummaryRow}>
                <span><FiGift style={{ verticalAlign: 'middle', marginRight: 4 }} />Gift Packing ({giftItems.length} item{giftItems.length > 1 ? 's' : ''})</span>
                <span>₹{giftCharges}</span>
              </div>
            )}

            <div className={styles.mobileSummaryRow}><span>Subtotal</span><span>₹{subtotal}</span></div>

            <div className={`${styles.mobileSummaryRow} ${styles.tax}`}>
              <span>CGST (2.5%)</span>
              <span>₹{cgst}</span>
            </div>
            <div className={`${styles.mobileSummaryRow} ${styles.tax}`}>
              <span>SGST (2.5%)</span>
              <span>₹{sgst}</span>
            </div>

            <div className={styles.mobileSummaryRow}>
              <span><FiTruck style={{ verticalAlign: 'middle', marginRight: 4 }} />Delivery Fee</span>
              <span className={deliveryCharge === 0 ? styles.free : ''}>
                {deliveryCharge === 0 ? 'FREE' : `₹${deliveryCharge}`}
              </span>
            </div>

            {deliveryCharge > 0 && (
              <div className={styles.mobileSummaryRow} style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                <span>Add ₹{FREE_DELIVERY_THRESHOLD - subtotal} more for free delivery</span>
              </div>
            )}

            <CouponBlock mobile {...couponProps} />

            {discount > 0 && (
              <div className={`${styles.mobileSummaryRow} ${styles.mobileDiscount}`}>
                <span>Discount</span>
                <span className={styles.discount}>-₹{discount}</span>
              </div>
            )}

            <div className={`${styles.mobileSummaryRow} ${styles.mobileTotalRow}`}>
              <span>Total</span><strong>₹{calculateTotal()}</strong>
            </div>

            {giftItems.length > 0 && (
              <div className={styles.giftSummaryBox}>
                <p className={styles.giftSummaryTitle}><FiGift /> Gift Messages</p>
                {giftItems.map(item => (
                  <div key={item.id} className={styles.giftSummaryItem}>
                    <span className={styles.giftSummaryProduct}>{item.products.name}:</span>
                    <span className={styles.giftSummaryQuote}>
                      {item.gift_quote ? `"${item.gift_quote}"` : <em>No message</em>}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* always "Pay Now" now */}
            <button onClick={handlePlaceOrder} className={styles.mobilePlaceOrderBtn} disabled={submitting || !selectedAddress}>
              {submitting ? 'Processing...' : `Pay Now • ₹${calculateTotal()}`}
            </button>
          </div>
        </div>

        {message && <div className={`${styles.mobileMessage} ${styles[message.type]}`}>{message.text}</div>}
      </div>
    );
  }

  // ============================================
  // DESKTOP CHECKOUT UI
  // ============================================
  return (
    <div className={styles.checkoutPage}>
      <div className={styles.container}>
        <Link to="/cart" className={styles.backLink}><FiArrowLeft /> Back to Cart</Link>
        <h1 className={styles.pageTitle}>Checkout</h1>
        {message && <div className={`${styles.message} ${styles[message.type]}`}>{message.text}</div>}

        <div className={styles.checkoutLayout}>
          <div className={styles.checkoutLeft}>
            {/* Address Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}><FiMapPin /><h2>Shipping Address</h2></div>
              {savedAddresses.map((address, idx) => (
                <div key={idx} className={`${styles.addressCard} ${selectedAddress?.id === address.id ? styles.selected : ''}`} onClick={() => setSelectedAddress(address)}>
                  <div className={styles.addressRadio}>{selectedAddress?.id === address.id && <FiCheck />}</div>
                  <div>
                    <p><strong>{address.name}</strong> 📞 {address.mobile}</p>
                    <p>{address.doorNo}, {address.street}</p>
                    <p>{address.city}, {address.state} - {address.pincode}</p>
                  </div>
                  <div className={styles.addressActions}>
                    <button onClick={(e) => { e.stopPropagation(); handleEditAddress(address, idx); }}><FiEdit2 /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteAddress(idx); }}><FiTrash2 /></button>
                  </div>
                </div>
              ))}
              <button className={styles.addAddressBtn} onClick={() => setShowNewAddress(!showNewAddress)}><FiPlus /> Add New Address</button>
              {showNewAddress && <AddressForm {...addressFormProps} />}
            </div>

            {/* Payment Method — Razorpay only, no selection needed */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}><FiCreditCard /><h2>Payment Method</h2></div>
              <div className={styles.paymentOptions}>
                <div className={`${styles.paymentOption} ${styles.selected}`}>
                  <input type="radio" checked readOnly /> Razorpay (Card/UPI/NetBanking)
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <FiPackage /><h2>Order Items ({totalItemsCount})</h2>
              </div>

              {cartItems.map(item => {
                const salePrice = getDiscountedPrice(item.products);
                const isOnSale = salePrice < item.products.price;
                const lineTotal = salePrice * item.quantity + (item.gift_packing ? GIFT_PACKING_CHARGE : 0);
                return (
                  <div key={`product-${item.id}`} className={styles.orderItem}>
                    <span>
                      {item.products.name} x{item.quantity}
                      {item.gift_packing && <span className={styles.giftTag}> 🎁 Gift Packed</span>}
                      {isOnSale && <span style={{ color: '#1F5B3A', fontSize: '0.8rem', marginLeft: 6 }}>(On Sale)</span>}
                    </span>
                    <span>
                      {isOnSale && (
                        <span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 6, fontSize: '0.85em' }}>
                          ₹{item.products.price * item.quantity}
                        </span>
                      )}
                      ₹{Math.round(lineTotal)}
                    </span>
                  </div>
                );
              })}

              {cartHampers.map(h => (
                <div key={`hamper-${h.id}`} className={styles.orderItem}>
                  <span>
                    <FiGift style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {h.hampers?.name} x{h.quantity}
                    <span className={styles.giftTag}> 🎁 Hamper</span>
                  </span>
                  <span>₹{(h.hampers?.price || 0) * h.quantity}</span>
                </div>
              ))}

              {giftItems.length > 0 && (
                <div className={styles.giftSummaryBox}>
                  <p className={styles.giftSummaryTitle}><FiGift /> Gift Messages</p>
                  {giftItems.map(item => (
                    <div key={item.id} className={styles.giftSummaryItem}>
                      <span className={styles.giftSummaryProduct}>{item.products.name}:</span>
                      <span className={styles.giftSummaryQuote}>
                        {item.gift_quote ? `"${item.gift_quote}"` : <em>No message added</em>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className={styles.checkoutRight}>
            <div className={styles.orderSummary}>
              <h3>Price Details</h3>
              <div className={styles.summaryDetails}>
                <div className={styles.summaryRow}>
                  <span>Price ({totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'})</span>
                  <span>₹{subtotal}</span>
                </div>
                {giftCharges > 0 && (
                  <div className={styles.summaryRow}>
                    <span><FiGift style={{ verticalAlign: 'middle', marginRight: 4 }} />Gift Packing</span>
                    <span>₹{giftCharges}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.tax}`}>
                  <span>CGST (2.5%)</span>
                  <span>₹{cgst}</span>
                </div>
                <div className={`${styles.summaryRow} ${styles.tax}`}>
                  <span>SGST (2.5%)</span>
                  <span>₹{sgst}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span><FiTruck style={{ verticalAlign: 'middle', marginRight: 4 }} />Delivery Fee</span>
                  <span className={deliveryCharge === 0 ? styles.free : ''}>
                    {deliveryCharge === 0 ? 'FREE' : `₹${deliveryCharge}`}
                  </span>
                </div>
                {deliveryCharge > 0 && (
                  <div className={styles.summaryRow} style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    <span>Add ₹{FREE_DELIVERY_THRESHOLD - subtotal} more for free delivery</span>
                  </div>
                )}

                <CouponBlock {...couponProps} />

                {discount > 0 && (
                  <div className={`${styles.summaryRow} ${styles.discountRow}`}>
                    <span>Discount</span>
                    <span className={styles.discount}>-₹{discount}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.total}`}>
                  <span>Total Amount</span>
                  <span>₹{calculateTotal()}</span>
                </div>
              </div>

              {deliveryCharge > 0 && (
                <div className={styles.deliveryThresholdMessage}>
                  <FiTruck />
                  <span>Add ₹{FREE_DELIVERY_THRESHOLD - subtotal} more to get FREE Delivery</span>
                </div>
              )}

              {/* always "Proceed to Pay" now */}
              <button onClick={handlePlaceOrder} className={styles.placeOrderBtn} disabled={submitting || !selectedAddress}>
                {submitting ? 'Processing...' : 'Proceed to Pay →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;